// src/pages/KundliPage.js — Full 22-Section Professional Kundali with Hindi
import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { Alert, Spinner, PageHeader, EmptyState, LoadingScreen } from '../components/ui';

const PLANETS  = ['Sun','Moon','Mercury','Venus','Mars','Jupiter','Saturn','Rahu','Ketu'];
const P_SYMBOL = { Sun:'☀',Moon:'☽',Mercury:'☿',Venus:'♀',Mars:'♂',Jupiter:'♃',Saturn:'♄',Rahu:'☊',Ketu:'☋' };
const P_SHORT  = { Sun:'Su',Moon:'Mo',Mercury:'Me',Venus:'Ve',Mars:'Ma',Jupiter:'Ju',Saturn:'Sa',Rahu:'Ra',Ketu:'Ke' };
const P_HI     = { Sun:'सूर्य',Moon:'चन्द्र',Mercury:'बुध',Venus:'शुक्र',Mars:'मंगल',Jupiter:'गुरु',Saturn:'शनि',Rahu:'राहु',Ketu:'केतु' };
const SIGNS_HI = { Aries:'मेष',Taurus:'वृष',Gemini:'मिथुन',Cancer:'कर्क',Leo:'सिंह',Virgo:'कन्या',Libra:'तुला',Scorpio:'वृश्चिक',Sagittarius:'धनु',Capricorn:'मकर',Aquarius:'कुम्भ',Pisces:'मीन' };
const SIGN_SYM = { Aries:'♈',Taurus:'♉',Gemini:'♊',Cancer:'♋',Leo:'♌',Virgo:'♍',Libra:'♎',Scorpio:'♏',Sagittarius:'♐',Capricorn:'♑',Aquarius:'♒',Pisces:'♓' };

// ── North Indian Chart ──────────────────────────────────
function NorthChart({ chart, title, hi }) {
  const hs = chart.house_signs || {};
  const pos = chart.planet_positions || {};
  const person = chart.person || {};

  const getPlanets = (h) =>
    PLANETS.filter(p => pos[p]?.house === h)
           .map(p => P_SHORT[p] + (pos[p]?.retrograde ? 'R' : ''))
           .join(' ');

  const Cell = ({ h, isCenter }) => {
    if (isCenter) return (
      <td colSpan={2} rowSpan={2}
        className="border border-amber-700/60 bg-amber-50/10 text-center align-middle p-2">
        <div className="font-cinzel text-amber-400 font-bold text-sm leading-tight">{person.name}</div>
        <div className="text-amber-600/60 text-xs mt-1">{person.dob}</div>
        <div className="text-amber-500 text-2xl mt-1">ॐ</div>
      </td>
    );
    const sign = hs[h] || '';
    const signShow = hi ? (SIGNS_HI[sign] || sign) : sign;
    const ps = getPlanets(h);
    return (
      <td className="border border-amber-700/40 text-center align-middle p-1 bg-cosmic-900/30 min-w-[60px]"
          style={{ minHeight: 60 }}>
        <div className="text-xs text-amber-700/60">{h}</div>
        <div className="text-xs font-bold text-amber-400">{signShow}</div>
        {ps && <div className="text-xs text-red-400 font-semibold mt-0.5">{ps}</div>}
      </td>
    );
  };

  return (
    <div className="border-2 border-amber-700/50 overflow-hidden rounded-lg">
      <div className="bg-amber-900/60 text-amber-300 text-center py-2 font-cinzel text-xs tracking-widest">{title}</div>
      <table className="w-full border-collapse">
        <tbody>
          <tr><Cell h={12}/><Cell h={1}/><Cell h={2}/><Cell h={3}/></tr>
          <tr><Cell h={11}/><Cell isCenter /><Cell h={4}/></tr>
          <tr><Cell h={10}/><Cell h={5}/></tr>
          <tr><Cell h={9}/><Cell h={8}/><Cell h={7}/><Cell h={6}/></tr>
        </tbody>
      </table>
    </div>
  );
}

// ── Section Wrapper ─────────────────────────────────────
function Section({ icon, title, children, id }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="cosmic-card overflow-hidden" id={id}>
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3 bg-amber-900/40 hover:bg-amber-900/60 transition-colors">
        <h3 className="font-cinzel font-bold text-amber-300 tracking-wide text-sm flex items-center gap-2">
          <span>{icon}</span>{title}
        </h3>
        <span className="text-amber-500 text-lg">{open ? '▲' : '▼'}</span>
      </button>
      {open && <div className="p-5">{children}</div>}
    </div>
  );
}

// ── InfoRow ─────────────────────────────────────────────
const InfoRow = ({ label, value, highlight, bad, good }) => (
  <div className="flex justify-between items-start py-1.5 border-b border-cosmic-800/30 gap-4">
    <span className="text-cosmic-400 text-sm flex-shrink-0">{label}</span>
    <span className={`text-sm font-semibold text-right ${highlight ? 'text-yellow-400' : bad ? 'text-red-400' : good ? 'text-green-400' : 'text-cosmic-200'}`}>{value}</span>
  </div>
);

// ── Main Component ──────────────────────────────────────
export default function KundliPage() {
  const [form, setForm] = useState({ name:'', dob:'', time_of_birth:'', place_of_birth:'', gender:'Male' });
  const [loading, setLoading]         = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [downloading, setDownloading] = useState('');
  const [error, setError]             = useState('');
  const [result, setResult]           = useState(null);
  const [kundlis, setKundlis]         = useState([]);
  const [tab, setTab]                 = useState('list');
  const [activeId, setActiveId]       = useState(null);
  const [hi, setHi]                   = useState(false); // Hindi toggle

  useEffect(() => { fetchList(); }, []);

  const fetchList = async () => {
    try { const { data } = await api.get('/kundli/kundli.php'); setKundlis(data.kundlis || []); }
    catch(e) {}
    setListLoading(false);
  };

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const deleteKundli = async (id, name) => {
    if (!window.confirm(`Delete Kundali for "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete('/kundli/kundli.php?id=' + id);
      fetchList();
      if (activeId === id) { setResult(null); setActiveId(null); setTab('list'); }
    } catch(e) { alert('Delete failed: ' + (e.response?.data?.message || e.message)); }
  };

  const handleSubmit = async e => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const { data } = await api.post('/kundli/kundli.php', form);
      setResult(data);
      setActiveId(data.kundli_id);
      setTab('view');
      fetchList();
    } catch(err) { setError(err.response?.data?.message || 'Failed to generate Kundali'); }
    finally { setLoading(false); }
  };

  const loadKundli = async (id) => {
    setListLoading(true);
    try {
      const { data } = await api.get('/kundli/kundli.php?id=' + id);
      setResult({ chart_data: data.kundli.chart_data, kundli_id: id });
      setActiveId(id); setTab('view');
    } catch(e) {}
    setListLoading(false);
  };

  const download = async (format) => {
    if (!activeId) return;
    setDownloading(format);
    try {
      const token = localStorage.getItem('astro_token');
      const lang  = hi ? 'hi' : 'en';
      const apiBase = window.location.hostname==='localhost'||window.location.hostname==='127.0.0.1' ? 'http://localhost/astrology/api' : 'https://astrology.infotrix.in/api';
      const base  = `${apiBase}/kundli/download.php?id=${activeId}&lang=${lang}&token=${encodeURIComponent(token)}`;

      if (format === 'pdf') {
        // Open directly in new tab - has all CSS, fonts load, then print dialog
        const win = window.open(base + '&autoprint=1', '_blank');
        if (!win) alert('Please allow popups for this site to download PDF.');
      } else {
        // Word: fetch HTML with token in header, save as .doc
        const res = await fetch(base, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) { alert('Download failed: ' + res.status + ' - check you are logged in'); return; }
        const html = await res.text();
        const wordHtml = html.replace('<html', '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word"');
        const blob = new Blob(['﻿' + wordHtml], { type: 'application/msword' });
        const name = chart?.person?.name || 'Kundali';
        const dob  = chart?.person?.dob  || '';
        const a    = document.createElement('a');
        a.href     = URL.createObjectURL(blob);
        a.download = `JanamKundali_${name}_${dob}_${lang === 'hi' ? 'Hindi' : 'English'}.doc`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
      }
    } catch(e) { alert('Download failed: ' + e.message); }
    setDownloading('');
  };

  const chart = result?.chart_data;

  // Helper: translate label based on hi toggle
  const T = (en, hindi) => hi ? hindi : en;

  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader icon="📜" title={T('Janam Kundali','जन्म कुण्डली')}
        subtitle={T('Professional 22-Section Vedic Birth Chart','22-खंड वैदिक जन्म पत्रिका')} />

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          {[['list', T('📋 My Kundalis','📋 मेरी कुण्डलियाँ')],
            ['new',  T('✨ Generate New','✨ नई बनाएं')]].map(([id,label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={'px-4 py-2 rounded-lg text-sm font-semibold transition-all ' + (tab===id?'bg-amber-700 text-white':'text-cosmic-400 hover:text-white hover:bg-cosmic-800/50')}>
              {label}
            </button>
          ))}
          {result && (
            <button onClick={() => setTab('view')}
              className={'px-4 py-2 rounded-lg text-sm font-semibold transition-all ' + (tab==='view'?'bg-amber-700 text-white':'text-cosmic-400 hover:text-white hover:bg-cosmic-800/50')}>
              🔭 {T('View Chart','चार्ट देखें')}
            </button>
          )}
        </div>
        {/* Hindi Toggle */}
        {tab === 'view' && chart && (
          <button onClick={() => setHi(h => !h)}
            className={'flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm border transition-all ' + (hi ? 'bg-amber-700 text-white border-amber-600' : 'border-amber-700/50 text-amber-400 hover:bg-amber-900/30')}>
            <span>{hi ? '🇮🇳 हिंदी' : '🇮🇳 हिंदी में देखें'}</span>
          </button>
        )}
      </div>

      {/* ── LIST ── */}
      {tab === 'list' && (
        listLoading ? <LoadingScreen message="Loading..." /> :
        kundlis.length === 0 ? (
          <div className="cosmic-card p-8">
            <EmptyState icon="📜" message={T('No Kundalis yet','अभी तक कोई कुण्डली नहीं')}
              sub={T('Generate your first Janam Kundali','अपनी पहली जन्म कुण्डली बनाएं')} />
            <div className="text-center mt-4">
              <button onClick={() => setTab('new')} className="btn-cosmic">✨ {T('Generate Kundali','कुण्डली बनाएं')}</button>
            </div>
          </div>
        ) : (
          <div className="grid gap-4">
            {kundlis.map(k => (
              <div key={k.id} className="cosmic-card p-5 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-cinzel font-bold text-white text-lg">{k.name}</p>
                  <p className="text-sm text-cosmic-400 mt-1">
                    📅 {k.dob} &nbsp;|&nbsp; 🕐 {k.time_of_birth} &nbsp;|&nbsp; 📍 {k.place_of_birth}
                    {k.gender && <span className="ml-2">| {k.gender}</span>}
                  </p>
                  <p className="text-xs text-cosmic-500 mt-1">
                    {T('Generated:','निर्मित:')} {new Date(k.created_at).toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'})}
                  </p>
                </div>
                <button onClick={() => loadKundli(k.id)} className="btn-cosmic text-sm py-2 px-5 flex-shrink-0">
                  {T('View & Download','देखें और डाउनलोड करें')}
                </button>
              </div>
            ))}
          </div>
        )
      )}

      {/* ── NEW FORM ── */}
      {tab === 'new' && (
        <div className="cosmic-card p-8 max-w-2xl">
          <h2 className="font-cinzel text-xl font-bold text-white mb-6">
            {T('Enter Birth Details','जन्म विवरण दर्ज करें')}
          </h2>
          {error && <div className="mb-5"><Alert type="error">{error}</Alert></div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="cosmic-label">{T('Full Name','पूरा नाम')}</label>
                <input name="name" required className="cosmic-input"
                  placeholder={T('e.g. Arjun Kumar Sharma','जैसे: अर्जुन कुमार शर्मा')}
                  value={form.name} onChange={handleChange} />
              </div>
              <div>
                <label className="cosmic-label">{T('Gender','लिंग')}</label>
                <select name="gender" className="cosmic-input" value={form.gender} onChange={handleChange}>
                  <option value="Male">{T('Male','पुरुष')}</option>
                  <option value="Female">{T('Female','स्त्री')}</option>
                  <option value="Other">{T('Other','अन्य')}</option>
                </select>
              </div>
              <div>
                <label className="cosmic-label">{T('Date of Birth','जन्म तिथि')}</label>
                <input name="dob" type="date" required className="cosmic-input"
                  value={form.dob} onChange={handleChange} />
              </div>
              <div>
                <label className="cosmic-label">{T('Time of Birth','जन्म समय')}</label>
                <input name="time_of_birth" type="time" required className="cosmic-input"
                  value={form.time_of_birth} onChange={handleChange} />
              </div>
              <div className="sm:col-span-2">
                <label className="cosmic-label">{T('Place of Birth','जन्म स्थान')}</label>
                <input name="place_of_birth" required className="cosmic-input"
                  placeholder={T('e.g. Mumbai, Maharashtra, India','जैसे: मुंबई, महाराष्ट्र, भारत')}
                  value={form.place_of_birth} onChange={handleChange} />
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn-cosmic w-full py-3 text-base mt-2">
              {loading
                ? <span className="flex items-center justify-center gap-2"><Spinner size="sm" /> {T('Generating...','बना रहे हैं...')}</span>
                : T('🔮 Generate Professional Kundali','🔮 पेशेवर कुण्डली बनाएं')}
            </button>
          </form>
        </div>
      )}

      {/* ── CHART VIEW ── */}
      {tab === 'view' && chart && (
        <div className="space-y-5">

          {/* Download + Hindi Bar */}
          <div className="cosmic-card p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h2 className="font-cinzel text-xl font-bold text-white">{chart.person?.name}</h2>
              <p className="text-cosmic-400 text-sm mt-1">
                {chart.person?.dob} &bull; {chart.person?.tob} &bull; {chart.person?.pob} &bull; {chart.person?.gender}
              </p>
              <div className="flex gap-2 mt-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-900/30 text-green-400 border border-green-500/30">
                  ✓ Real VSOP87 Ephemeris
                </span>
                {chart.coordinates_used && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-900/30 text-blue-400 border border-blue-500/30">
                    📍 {chart.coordinates_used.place} ({chart.coordinates_used.lat?.toFixed(2)}°, {chart.coordinates_used.lon?.toFixed(2)}°)
                  </span>
                )}
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-900/30 text-amber-400 border border-amber-500/30">
                  Lahiri Ayanamsa: {chart.ayanamsa?.value}°
                </span>
              </div>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button onClick={() => setHi(h => !h)}
                className={'px-4 py-2 rounded-lg font-semibold text-sm border transition-all ' + (hi ? 'bg-amber-700 text-white border-amber-600' : 'border-amber-700/50 text-amber-400 hover:bg-amber-900/30')}>
                🇮🇳 {hi ? T('Switch to English','अंग्रेज़ी में देखें') : T('हिंदी में देखें','हिंदी में देखें')}
              </button>
              <button onClick={() => download('pdf')} disabled={downloading==='pdf'}
                className="btn-cosmic flex items-center gap-2 py-2.5 px-5">
                {downloading==='pdf' ? <Spinner size="sm"/> : '📄'}
                {downloading==='pdf' ? T('Preparing...','तैयार हो रहा है...') : T('PDF Download','PDF डाउनलोड')}
              </button>
              <button onClick={() => download('word')} disabled={downloading==='word'}
                className="btn-gold flex items-center gap-2 py-2.5 px-5">
                {downloading==='word' ? <Spinner size="sm"/> : '📝'}
                {downloading==='word' ? T('Preparing...','तैयार हो रहा है...') : T('Word Download','Word डाउनलोड')}
              </button>
            </div>
          </div>

          {hi && (
            <div className="cosmic-card p-3 border border-amber-700/40 bg-amber-900/10">
              <p className="text-amber-400 text-sm text-center font-semibold">
                🇮🇳 हिंदी मोड सक्रिय — सभी विवरण हिंदी में दिखाए जा रहे हैं। PDF/Word भी हिंदी में डाउनलोड होगा।
              </p>
            </div>
          )}

          {/* ─ Summary Strip ─ */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            {[
              [T('Ascendant','लग्न'),         hi?(chart.ascendant_hi||chart.ascendant):chart.ascendant,     '⬆️'],
              [T('Moon Sign','राशि'),          hi?(chart.moon_sign_hi||chart.moon_sign):chart.moon_sign,     '🌙'],
              [T('Sun Sign','सूर्य राशि'),     hi?(chart.sun_sign_hi||chart.sun_sign):chart.sun_sign,        '☀️'],
              [T('Nakshatra','नक्षत्र'),       hi?(chart.nakshatra_hi||chart.nakshatra):chart.nakshatra,     '⭐'],
              [T('Pada','पाद'),                chart.nakshatra_pada,                                         '🔢'],
              [T('Element','तत्व'),            chart.element,                                                '🌊'],
              [T('Ruling Planet','शासक'),      chart.ruling_planet,                                          '🪐'],
              [T('Manglik','मांगलिक'),         chart.manglik ? T('Yes','हाँ') : T('No','नहीं'),              chart.manglik ? '🔴' : '🟢'],
            ].map(([label, val, icon]) => (
              <div key={label} className="cosmic-card p-2 text-center border border-amber-700/30">
                <div className="text-lg mb-0.5">{icon}</div>
                <p className="text-xs text-amber-600/70 mb-0.5 leading-tight">{label}</p>
                <p className="font-cinzel font-bold text-amber-300 text-xs leading-tight">{val}</p>
              </div>
            ))}
          </div>

          {/* ─ Section 1: Panchang ─ */}
          <Section icon="🗓️" title={T('1. Panchang Details','1. पंचांग विवरण')} id="panchang">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-1">
                <InfoRow label={T('Tithi (Lunar Day)','तिथि')} value={hi?(chart.panchang?.tithi_hi||chart.panchang?.tithi):chart.panchang?.tithi} />
                <InfoRow label={T('Vaar (Weekday)','वार')}     value={hi?(chart.panchang?.vaar_hi||chart.panchang?.vaar):chart.panchang?.vaar} />
                <InfoRow label={T('Nakshatra','नक्षत्र')}      value={hi?(chart.panchang?.nakshatra_hi||chart.panchang?.nakshatra):chart.panchang?.nakshatra} />
                <InfoRow label={T('Nakshatra Lord','नक्षत्र स्वामी')} value={chart.panchang?.nakshatra_lord} />
              </div>
              <div className="space-y-1">
                <InfoRow label={T('Yoga (Panchang)','योग')}   value={hi?(chart.panchang?.yoga_hi||chart.panchang?.yoga_panchang):chart.panchang?.yoga_panchang} />
                <InfoRow label={T('Karana','करण')}            value={hi?(chart.panchang?.karana_hi||chart.panchang?.karana):chart.panchang?.karana} />
                <InfoRow label={T('Samvat','संवत')}           value={chart.panchang?.samvat} />
                <InfoRow label={T('Masa','मास')}              value={chart.panchang?.masa} />
              </div>
              <div className="space-y-1">
                <InfoRow label={T('Ayanamsa System','अयनांश पद्धति')} value={hi?(chart.ayanamsa?.system_hi||chart.ayanamsa?.system):chart.ayanamsa?.system} />
                <InfoRow label={T('Ayanamsa Value','अयनांश मान')}     value={chart.ayanamsa?.value + '°'} />
                <InfoRow label={T('Zodiac Type','राशि चक्र')}         value={hi?(chart.ayanamsa?.zodiac_hi||chart.ayanamsa?.zodiac):chart.ayanamsa?.zodiac} />
              </div>
            </div>
          </Section>

          {/* ─ Section 3: Lagna Chart ─ */}
          <Section icon="🔮" title={T('Birth Chart — Kundali Chakra','जन्म कुण्डली चक्र')} id="chart">
            <div className="grid lg:grid-cols-2 gap-6">
              <NorthChart chart={chart} title={T('Lagna Chart (D-1)','लग्न कुण्डली (D-1)')} hi={hi} />
              <NorthChart
                chart={{ ...chart, house_signs: buildNavamsa(chart), planet_positions: {} }}
                title={T('Navamsa Chart (D-9)','नवांश कुण्डली (D-9)')} hi={hi} />
            </div>
            <div className="mt-3 text-xs text-cosmic-500 text-center italic">
              {T('Su=Sun Mo=Moon Me=Mercury Ve=Venus Ma=Mars Ju=Jupiter Sa=Saturn Ra=Rahu Ke=Ketu R=Retrograde',
                 'Su=सूर्य Mo=चन्द्र Me=बुध Ve=शुक्र Ma=मंगल Ju=गुरु Sa=शनि Ra=राहु Ke=केतु R=वक्री')}
            </div>
          </Section>

          {/* ─ Section 3: Planetary Positions ─ */}
          <Section icon="🪐" title={T('3. Planetary Positions (Exact Degrees)','3. ग्रहों की स्थिति (सटीक डिग्री)')} id="planets">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-900/40">
                    {[T('Planet','ग्रह'),T('Sign','राशि'),T('House','भाव'),T('Degree','डिग्री'),T('Full Position','पूर्ण स्थिति'),T('Status','स्थिति')].map(h => (
                      <th key={h} className="text-left text-amber-400 px-3 py-2 font-cinzel text-xs">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PLANETS.map(p => {
                    const pp = chart.planet_positions?.[p];
                    if (!pp) return null;
                    const name = hi ? (P_HI[p]||p) : p;
                    const sign = hi ? (pp.sign_hi||pp.sign) : pp.sign;
                    return (
                      <tr key={p} className="border-b border-cosmic-800/30 hover:bg-amber-900/10">
                        <td className="px-3 py-2.5 font-semibold text-white">
                          <span className="text-lg mr-1">{P_SYMBOL[p]}</span>{name}
                        </td>
                        <td className="px-3 py-2.5 text-cosmic-200">{SIGN_SYM[pp.sign]} {sign}</td>
                        <td className="px-3 py-2.5 text-cosmic-300">{T('House','भाव')} {pp.house}</td>
                        <td className="px-3 py-2.5 text-amber-400 font-mono text-xs">{pp.degrees}°{pp.minutes}'{pp.seconds}"</td>
                        <td className="px-3 py-2.5 text-cosmic-400 text-xs">{pp.position}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex gap-1 flex-wrap">
                            {pp.retrograde && <span className="badge bg-red-900/30 text-red-400 border-red-500/30 text-xs">{T('Retro','वक्री')}</span>}
                            {pp.combust   && <span className="badge bg-orange-900/30 text-orange-400 border-orange-500/30 text-xs">{T('Combust','अस्त')}</span>}
                            {!pp.retrograde && !pp.combust && <span className="badge bg-green-900/30 text-green-400 border-green-500/30 text-xs">{T('Direct','मार्गी')}</span>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>

          {/* ─ Section 4: Shadbala ─ */}
          <Section icon="💪" title={T('4. Shadbala — Planetary Strength','4. षड्बल — ग्रह शक्ति विश्लेषण')} id="shadbala">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-900/40">
                    {[T('Planet','ग्रह'),T('Sthan Bala','स्थान बल'),T('Dig Bala','दिग् बल'),T('Kala Bala','काल बल'),T('Chesta Bala','चेष्टा बल'),T('Total','कुल'),T('Strength','शक्ति')].map(h => (
                      <th key={h} className="text-left text-amber-400 px-3 py-2 font-cinzel text-xs">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PLANETS.map(p => {
                    const s = chart.shadbala?.[p];
                    if (!s) return null;
                    const name = hi ? (P_HI[p]||p) : p;
                    return (
                      <tr key={p} className="border-b border-cosmic-800/30 hover:bg-amber-900/10">
                        <td className="px-3 py-2 font-semibold text-white text-sm">{P_SYMBOL[p]} {name}</td>
                        <td className="px-3 py-2 text-cosmic-300">{s.sthan_bala}</td>
                        <td className="px-3 py-2 text-cosmic-300">{s.dig_bala}</td>
                        <td className="px-3 py-2 text-cosmic-300">{s.kala_bala}</td>
                        <td className="px-3 py-2 text-cosmic-300">{s.chesta_bala}</td>
                        <td className="px-3 py-2 font-bold text-amber-400">{s.total}</td>
                        <td className="px-3 py-2">
                          <span className={'badge ' + (s.strong ? 'bg-green-900/30 text-green-400 border-green-500/30' : 'bg-yellow-900/30 text-yellow-400 border-yellow-500/30')}>
                            {s.strong ? T('Strong','बलवान') : T('Moderate','मध्यम')}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>

          {/* ─ Section 5: Ashtakavarga ─ */}
          <Section icon="🔢" title={T('5. Ashtakavarga — Bindu Scores','5. अष्टकवर्ग — बिंदु तालिका')} id="ashtaka">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-amber-900/40">
                    <th className="text-amber-400 px-2 py-2 font-cinzel text-left">{T('Planet','ग्रह')}</th>
                    {Object.keys(chart.sarvashtakavarga||{}).map(s => (
                      <th key={s} className="text-amber-400 px-1 py-2 text-center">{SIGN_SYM[s]||s.slice(0,3)}</th>
                    ))}
                    <th className="text-amber-400 px-2 py-2">{T('Total','कुल')}</th>
                  </tr>
                </thead>
                <tbody>
                  {PLANETS.map(p => {
                    const a = chart.ashtakavarga?.[p];
                    if (!a) return null;
                    const name = hi ? (P_HI[p]||p) : p;
                    return (
                      <tr key={p} className="border-b border-cosmic-800/30">
                        <td className="px-2 py-1.5 font-semibold text-white">{P_SYMBOL[p]} {name}</td>
                        {Object.values(a.bindus||{}).map((b,i) => (
                          <td key={i} className={'text-center px-1 py-1.5 ' + (b>=6?'text-green-400 font-bold':b<=3?'text-red-400':'text-cosmic-300')}>{b}</td>
                        ))}
                        <td className="px-2 py-1.5 font-bold text-amber-400">{a.total}</td>
                      </tr>
                    );
                  })}
                  <tr className="bg-amber-900/30">
                    <td className="px-2 py-1.5 font-bold text-amber-300">{T('Sarva','सर्व')}</td>
                    {Object.values(chart.sarvashtakavarga||{}).map((v,i) => (
                      <td key={i} className={'text-center px-1 py-1.5 font-bold ' + (v>=28?'text-green-400':v<=20?'text-red-400':'text-amber-300')}>{v}</td>
                    ))}
                    <td></td>
                  </tr>
                </tbody>
              </table>
              <p className="text-xs text-cosmic-500 mt-2 italic">{T('6+ bindus = favorable, 3 or less = weak area','6+ बिंदु = शुभ, 3 या कम = कमजोर क्षेत्र')}</p>
            </div>
          </Section>

          {/* ─ Section 6-7: Aspects & Conjunctions ─ */}
          <Section icon="🔗" title={T('6-7. Aspects & Conjunctions','6-7. दृष्टि और युति')} id="aspects">
            <div className="grid lg:grid-cols-2 gap-5">
              <div>
                <h4 className="font-cinzel text-amber-400 text-sm mb-3">{T('Conjunctions (Yuti)','युति — एक भाव में ग्रह')}</h4>
                {chart.conjunctions?.length > 0 ? (
                  <div className="space-y-2">
                    {chart.conjunctions.map((cj,i) => (
                      <div key={i} className="p-3 bg-amber-900/10 border border-amber-700/30 rounded-lg">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="badge bg-amber-700/30 text-amber-300 border-amber-600/40">{T('House','भाव')} {cj.house}</span>
                          <span className="text-cosmic-200 font-semibold">
                            {cj.planets.map(p => hi?(P_HI[p]||p):p).join(' + ')}
                          </span>
                        </div>
                        <p className="text-amber-400 text-xs mt-1 font-semibold">{cj.yoga_name}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-cosmic-500 text-sm italic">{T('No conjunctions found','कोई युति नहीं मिली')}</p>
                )}
              </div>
              <div>
                <h4 className="font-cinzel text-amber-400 text-sm mb-3">{T('Retrograde & Combust','वक्री और अस्त ग्रह')}</h4>
                <div className="space-y-2">
                  <div className="p-3 bg-red-900/10 border border-red-700/30 rounded-lg">
                    <p className="text-xs text-red-400 font-bold mb-1">{T('Retrograde Planets (Vakri)','वक्री ग्रह')}</p>
                    <p className="text-cosmic-200 text-sm">
                      {(chart.retrograde_planets||[]).length > 0
                        ? (chart.retrograde_planets||[]).map(p=>hi?(P_HI[p]||p):p).join(', ')
                        : T('None','कोई नहीं')}
                    </p>
                  </div>
                  <div className="p-3 bg-orange-900/10 border border-orange-700/30 rounded-lg">
                    <p className="text-xs text-orange-400 font-bold mb-1">{T('Combust Planets (Asta)','अस्त ग्रह')}</p>
                    <p className="text-cosmic-200 text-sm">
                      {(chart.combust_planets||[]).length > 0
                        ? (chart.combust_planets||[]).map(cp=>(hi?(P_HI[cp.planet]||cp.planet):cp.planet)+' ('+cp.sign+')').join(', ')
                        : T('None','कोई नहीं')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* ─ Section 10: Divisional Charts ─ */}
          <Section icon="📊" title={T('10. Divisional Charts (Varga Charts)','10. वर्ग चार्ट')} id="varga">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.entries(chart.divisional_charts||{}).map(([key,dc]) => (
                <div key={key} className="cosmic-card p-3 text-center border border-amber-700/30">
                  <div className="font-cinzel text-amber-400 font-bold text-base">{key}</div>
                  <div className="text-xs text-cosmic-400 mt-1 leading-tight">
                    {hi ? (dc.name_hi||dc.name) : dc.name}
                  </div>
                  <div className="text-xs text-amber-600 mt-2">{T('Asc:','लग्न:')} {dc.ascendant}</div>
                </div>
              ))}
            </div>
          </Section>

          {/* ─ Section 11-12: Arudha & Upagrahas ─ */}
          <Section icon="🌑" title={T('11-12. Arudha Lagna & Upagrahas','11-12. आरूढ लग्न और उपग्रह')} id="arudha">
            <div className="grid lg:grid-cols-2 gap-5">
              <div>
                <h4 className="font-cinzel text-amber-400 text-sm mb-3">{T('Arudha Lagna (AL)','आरूढ लग्न')}</h4>
                <div className="space-y-1">
                  <InfoRow label={T('House','भाव')} value={chart.arudha_lagna?.house} />
                  <InfoRow label={T('Sign','राशि')} value={chart.arudha_lagna?.sign} />
                  <InfoRow label={T('Meaning','अर्थ')} value={hi?(chart.arudha_lagna?.meaning_hi||chart.arudha_lagna?.meaning):chart.arudha_lagna?.meaning} />
                </div>
              </div>
              <div>
                <h4 className="font-cinzel text-amber-400 text-sm mb-3">{T('Upagrahas (Shadow Planets)','उपग्रह')}</h4>
                <div className="space-y-1">
                  {Object.entries(chart.upagrahas||{}).map(([k,u]) => (
                    <InfoRow key={k} label={k} value={u.sign + ' (' + T('House','भाव') + ' ' + u.house + ')'} />
                  ))}
                </div>
              </div>
            </div>
          </Section>

          {/* ─ Section 13: Transits ─ */}
          <Section icon="🌍" title={T('13. Transit Analysis (Gochar)','13. गोचर विश्लेषण')} id="transits">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-900/40">
                    {[T('Planet','ग्रह'),T('Current Sign','वर्तमान राशि'),T('Natal Sign','जन्म राशि'),T('Effect','प्रभाव')].map(h=>(
                      <th key={h} className="text-left text-amber-400 px-3 py-2 font-cinzel text-xs">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PLANETS.map(p => {
                    const tr = chart.transits?.[p];
                    if (!tr) return null;
                    const name = hi ? (P_HI[p]||p) : p;
                    const obs  = hi ? (tr.observation_hi||tr.observation) : tr.observation;
                    return (
                      <tr key={p} className="border-b border-cosmic-800/30 hover:bg-amber-900/10">
                        <td className="px-3 py-2 font-semibold text-white">{P_SYMBOL[p]} {name}</td>
                        <td className="px-3 py-2 text-green-400">{tr.current_sign}</td>
                        <td className="px-3 py-2 text-cosmic-400">{tr.natal_sign}</td>
                        <td className="px-3 py-2 text-cosmic-300 text-xs">{obs}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>

          {/* ─ Section 14-15: Dasha ─ */}
          <Section icon="⏳" title={T('14-15. Vimshottari Dasha & Yogini Dasha','14-15. विंशोत्तरी दशा और योगिनी दशा')} id="dasha">
            <div className="grid lg:grid-cols-2 gap-5 mb-5">
              <div className="cosmic-card p-4 border border-amber-700/30">
                <h4 className="font-cinzel text-amber-400 text-sm mb-3">{T('Current Dasha Periods','वर्तमान दशा काल')}</h4>
                <div className="space-y-1">
                  <InfoRow label={T('Maha Dasha','महादशा')}   value={hi?(chart.dasha?.maha_dasha_hi||chart.dasha?.maha_dasha):chart.dasha?.maha_dasha} highlight />
                  <InfoRow label={T('Total Years','कुल वर्ष')} value={chart.dasha?.maha_dasha_years + ' ' + T('yrs','वर्ष')} />
                  <InfoRow label={T('Elapsed','बीते')}         value={chart.dasha?.years_elapsed + ' ' + T('yrs','वर्ष')} />
                  <InfoRow label={T('Remaining','शेष')}        value={chart.dasha?.years_remaining + ' ' + T('yrs','वर्ष')} good />
                  <InfoRow label={T('Antardasha','अंतर्दशा')} value={hi?(chart.dasha?.antardasha_hi||chart.dasha?.antardasha):chart.dasha?.antardasha} highlight />
                  <InfoRow label={T('Pratyantar','प्रत्यंतर')} value={hi?(chart.dasha?.pratyantar_hi||chart.dasha?.pratyantar):chart.dasha?.pratyantar} />
                </div>
              </div>
              <div className="cosmic-card p-4 border border-amber-700/30">
                <h4 className="font-cinzel text-amber-400 text-sm mb-3">{T('Yogini Dasha (Section 15)','योगिनी दशा')}</h4>
                <InfoRow label={T('Current Yogini','वर्तमान')} value={chart.yogini_dasha?.current} highlight />
                <InfoRow label={T('Period','अवधि')}            value={chart.yogini_dasha?.years + ' ' + T('years','वर्ष')} />
                <InfoRow label={T('Next Yogini','अगली')}       value={chart.yogini_dasha?.next} />
                <p className="text-cosmic-500 text-xs mt-3 italic">{T('Used for cross-verification with Vimshottari.','विंशोत्तरी की पुष्टि के लिए प्रयुक्त।')}</p>
              </div>
            </div>
            {/* Dasha Tree */}
            <h4 className="font-cinzel text-amber-400 text-sm mb-3">{T('Dasha Timeline','दशा कालरेखा')}</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-900/40">
                    {[T('Maha Dasha','महादशा'),T('Period','अवधि'),T('Start','आरंभ'),T('End','समाप्ति')].map(h=>(
                      <th key={h} className="text-left text-amber-400 px-3 py-2 font-cinzel text-xs">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(chart.dasha?.tree||[]).slice(0,4).map((d,i) => (
                    <React.Fragment key={i}>
                      <tr className="bg-amber-900/20 border-b border-amber-700/30">
                        <td className="px-3 py-2 font-bold text-amber-300">{P_SYMBOL[d.lord]||''} {hi?(P_HI[d.lord]||d.lord):d.lord}</td>
                        <td className="px-3 py-2 text-cosmic-200">{d.years} {T('yrs','वर्ष')}</td>
                        <td className="px-3 py-2 text-cosmic-400">{d.start}</td>
                        <td className="px-3 py-2 text-cosmic-400">{d.end}</td>
                      </tr>
                      {(d.antardashas||[]).slice(0,3).map((ad,j) => (
                        <tr key={j} className="border-b border-cosmic-800/30">
                          <td className="px-3 py-2 pl-8 text-cosmic-400 text-xs">↳ {hi?(P_HI[ad.lord]||ad.lord):ad.lord}</td>
                          <td className="px-3 py-2 text-cosmic-500 text-xs">{ad.months} {T('months','माह')}</td>
                          <td className="px-3 py-2 text-cosmic-500 text-xs">{ad.start}</td>
                          <td className="px-3 py-2 text-cosmic-500 text-xs">{ad.end}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* ─ Section 16: Special Yogas ─ */}
          <Section icon="✨" title={T('16. Special Yogas (Advanced)','16. विशेष योग')} id="yogas">
            <div className="grid gap-3">
              {(chart.special_yogas||[]).map((yg,i) => (
                <div key={i} className={'p-4 border rounded-xl ' + (yg.type==='benefic'?'border-green-700/40 bg-green-900/10':yg.type==='dosha'?'border-red-700/40 bg-red-900/10':'border-yellow-700/40 bg-yellow-900/10')}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-cinzel font-bold text-white">{hi?(yg.name_hi||yg.name):yg.name}</span>
                        <span className={'badge text-xs ' + (yg.type==='benefic'?'bg-green-900/30 text-green-400 border-green-500/30':yg.type==='dosha'?'bg-red-900/30 text-red-400 border-red-500/30':'bg-yellow-900/30 text-yellow-400 border-yellow-500/30')}>
                          {yg.type==='benefic'?T('Benefic','शुभ'):yg.type==='dosha'?T('Dosha','दोष'):T('Mixed','मिश्रित')}
                        </span>
                        <span className="badge bg-cosmic-700/40 text-cosmic-300 border-cosmic-600/30 text-xs">{yg.strength}</span>
                      </div>
                      <p className="text-cosmic-300 text-sm mt-2 italic">{hi?(yg.description_hi||yg.description):yg.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* ─ Section 17: Dosha ─ */}
          <Section icon="⚠️" title={T('17. Dosha Deep Analysis','17. दोष विश्लेषण')} id="dosha">
            <div className="grid lg:grid-cols-2 gap-5">
              <div>
                <h4 className="font-cinzel text-amber-400 text-sm mb-3">{T('Mangal Dosha (Kuja Dosha)','मांगलिक दोष')}</h4>
                <div className="space-y-1">
                  <InfoRow label={T('Status','स्थिति')}   value={chart.dosha_analysis?.mangal_dosha?.present ? T('Dosha Present','दोष उपस्थित') : T('No Dosha','दोष नहीं')}
                    bad={chart.dosha_analysis?.mangal_dosha?.present} good={!chart.dosha_analysis?.mangal_dosha?.present} />
                  <InfoRow label={T('Severity','तीव्रता')} value={chart.dosha_analysis?.mangal_dosha?.severity} />
                  <InfoRow label={T('Mars House','मंगल भाव')} value={chart.dosha_analysis?.mangal_dosha?.mars_house} />
                </div>
                <div className="mt-3 p-3 bg-amber-900/10 border border-amber-700/30 rounded-lg">
                  <p className="text-cosmic-300 text-xs italic">{hi?(chart.dosha_analysis?.mangal_dosha?.description_hi||chart.dosha_analysis?.mangal_dosha?.description):chart.dosha_analysis?.mangal_dosha?.description}</p>
                  <p className="text-amber-400 text-xs mt-2 font-semibold">
                    {T('Remedy: ','उपाय: ')}{hi?(chart.dosha_analysis?.mangal_dosha?.remedy_hi||chart.dosha_analysis?.mangal_dosha?.remedy):chart.dosha_analysis?.mangal_dosha?.remedy}
                  </p>
                </div>
              </div>
              <div>
                <h4 className="font-cinzel text-amber-400 text-sm mb-3">{T('Kaal Sarp & Sade Sati','काल सर्प और साढ़े साती')}</h4>
                <div className="space-y-1">
                  <InfoRow label={T('Kaal Sarp Type','काल सर्प प्रकार')} value={chart.dosha_analysis?.kaal_sarp_dosha?.type} />
                  <InfoRow label={T('Rahu House','राहु भाव')} value={chart.dosha_analysis?.kaal_sarp_dosha?.rahu_house} />
                  <InfoRow label={T('Ketu House','केतु भाव')} value={chart.dosha_analysis?.kaal_sarp_dosha?.ketu_house} />
                  <InfoRow label={T('Sade Sati','साढ़े साती')} value={chart.dosha_analysis?.shani_sade_sati?.phase} />
                </div>
                <div className="mt-3 p-3 bg-red-900/10 border border-red-700/30 rounded-lg">
                  <p className="text-cosmic-300 text-xs italic">{hi?(chart.dosha_analysis?.kaal_sarp_dosha?.description_hi||chart.dosha_analysis?.kaal_sarp_dosha?.description):chart.dosha_analysis?.kaal_sarp_dosha?.description}</p>
                </div>
              </div>
            </div>
          </Section>

          {/* ─ Section 18: Bhava Chalita ─ */}
          <Section icon="🏠" title={T('18. Bhava Chalita Chart','18. भाव चलित चार्ट')} id="bhava">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {Array.from({length:12},(_,i)=>i+1).map(h => {
                const b  = chart.bhava_chalita?.[h];
                const ps = PLANETS.filter(p => chart.planet_positions?.[p]?.house === h);
                const shifted = (b?.planets||[]).filter(pp=>pp.shifted);
                return (
                  <div key={h} className={'border rounded-xl p-3 ' + (shifted.length?'border-amber-600/60 bg-amber-900/15':'border-cosmic-700/30 bg-cosmic-800/20')}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs font-bold text-amber-500">{T('House','भाव')} {h}</span>
                      {shifted.length > 0 && <span className="text-xs text-amber-400">⚡{T('Shifted','बदला')}</span>}
                    </div>
                    <p className="text-sm font-semibold text-cosmic-200">{b?.rashi_sign}</p>
                    {shifted.length > 0 && (
                      <p className="text-xs text-amber-400 mt-1">
                        {shifted.map(sp=>(hi?(P_HI[sp.planet]||sp.planet):sp.planet)+'→H'+sp.chalita_house).join(', ')}
                      </p>
                    )}
                    {ps.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {ps.map(p=>(
                          <span key={p} className="text-xs bg-amber-700/20 text-amber-400 px-1 rounded">
                            {P_SHORT[p]}{chart.planet_positions?.[p]?.retrograde?'R':''}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>

          {/* ─ Section 19: KP Astrology ─ */}
          <Section icon="🔬" title={T('19. KP Astrology (Krishnamurti Paddhati)','19. KP ज्योतिष')} id="kp">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <InfoRow label={T('1st House Significator','प्रथम भाव सिग्नीफिकेटर')} value={chart.kp_astrology?.significator_1} />
                <InfoRow label={T('7th House Significator','सप्तम भाव सिग्नीफिकेटर')} value={chart.kp_astrology?.significator_7} />
              </div>
              <div className="space-y-1">
                <InfoRow label={T('Ascendant Sub-Lord','लग्न सब-लॉर्ड')} value={chart.kp_astrology?.sublord_asc} />
                <InfoRow label={T('Method','पद्धति')} value={chart.kp_astrology?.method} />
              </div>
            </div>
          </Section>

          {/* ─ Section 20: Life Predictions ─ */}
          <Section icon="🌟" title={T('20. Life Area Predictions','20. जीवन क्षेत्र भविष्यवाणी')} id="life">
            <div className="grid lg:grid-cols-2 gap-4">
              {[
                ['💼', T('Career','करियर'),           chart.life_predictions?.career?.summary,      chart.life_predictions?.career?.summary_hi],
                ['💍', T('Marriage','विवाह'),          chart.life_predictions?.marriage?.timing,     chart.life_predictions?.marriage?.timing_hi],
                ['💰', T('Finance','वित्त'),           chart.life_predictions?.finance?.overall,     chart.life_predictions?.finance?.overall_hi],
                ['🏥', T('Health','स्वास्थ्य'),       chart.life_predictions?.health?.constitution, chart.life_predictions?.health?.constitution_hi],
                ['✈️', T('Foreign Travel','विदेश यात्रा'), chart.life_predictions?.foreign_travel?.chance, chart.life_predictions?.foreign_travel?.chance_hi],
                ['🕉️', T('Spirituality','आध्यात्मिकता'), chart.life_predictions?.spirituality?.inclination, chart.life_predictions?.spirituality?.inclination_hi],
              ].map(([icon,label,val,valHi]) => (
                <div key={label} className="p-4 bg-amber-900/10 border border-amber-700/30 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{icon}</span>
                    <h4 className="font-cinzel font-bold text-amber-400 text-sm">{label}</h4>
                  </div>
                  <p className="text-cosmic-200 text-sm leading-relaxed">{hi ? (valHi||val) : val}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div className="p-3 bg-cosmic-800/30 rounded-xl">
                <p className="text-xs text-amber-500 font-bold mb-1">{T('Career Peak Years','करियर शिखर वर्ष')}</p>
                <p className="text-cosmic-200">{(chart.life_predictions?.career?.peak_years||[]).join(', ')}</p>
              </div>
              <div className="p-3 bg-cosmic-800/30 rounded-xl">
                <p className="text-xs text-amber-500 font-bold mb-1">{T('Finance Peak Period','वित्त शिखर काल')}</p>
                <p className="text-cosmic-200 text-sm">{chart.life_predictions?.finance?.peak_period}</p>
              </div>
            </div>
          </Section>

          {/* ─ Section 21: Gemstone & Remedies ─ */}
          <Section icon="💎" title={T('21. Gemstone & Remedy Science','21. रत्न और उपाय विज्ञान')} id="gems">
            <div className="grid lg:grid-cols-2 gap-5">
              <div>
                <h4 className="font-cinzel text-amber-400 text-sm mb-3">{T('Primary Gemstone','मुख्य रत्न')}</h4>
                <div className="p-4 bg-amber-900/20 border border-amber-700/40 rounded-xl">
                  <p className="font-cinzel text-2xl font-bold text-white">
                    {hi?(chart.gemstone_remedies?.primary_gem_hi||chart.gemstone_remedies?.primary_gem):chart.gemstone_remedies?.primary_gem}
                  </p>
                  <div className="mt-3 space-y-1">
                    <InfoRow label={T('For Planet','ग्रह के लिए')} value={chart.gemstone_remedies?.primary_planet} />
                    <InfoRow label={T('Weight','वजन')}             value={chart.gemstone_remedies?.weight} />
                    <InfoRow label={T('Finger','उंगली')}           value={hi?(chart.gemstone_remedies?.finger_hi||chart.gemstone_remedies?.finger):chart.gemstone_remedies?.finger} />
                    <InfoRow label={T('Day to Wear','पहनने का दिन')} value={hi?(chart.gemstone_remedies?.day_hi||chart.gemstone_remedies?.day_to_wear):chart.gemstone_remedies?.day_to_wear} />
                  </div>
                  <div className="mt-3 p-2 bg-cosmic-800/30 rounded-lg">
                    <p className="text-xs font-bold text-amber-400">{T('Mantra:','मंत्र:')}</p>
                    <p className="text-cosmic-200 text-xs mt-1">
                      {hi?(chart.gemstone_remedies?.mantra_hi||chart.gemstone_remedies?.mantra):chart.gemstone_remedies?.mantra}
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-cinzel text-amber-400 text-sm mb-3">{T('Vedic Remedies','वैदिक उपाय')}</h4>
                <div className="space-y-2">
                  {(hi?(chart.gemstone_remedies?.remedies_hi||chart.gemstone_remedies?.remedies):chart.gemstone_remedies?.remedies||[]).map((r,i) => (
                    <div key={i} className="flex items-start gap-2 p-2.5 bg-amber-900/10 border-l-4 border-amber-600 rounded-r-lg">
                      <span className="text-amber-400 font-bold text-xs mt-0.5">{i+1}.</span>
                      <p className="text-cosmic-200 text-sm italic">{r}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Section>

          {/* ─ Section 22: Personality ─ */}
          <Section icon="🧠" title={T('22. Personality Psychology & Self-Analysis','22. व्यक्तित्व मनोविज्ञान और आत्म-विश्लेषण')} id="personality">
            <div className="p-4 bg-amber-900/10 border border-amber-700/30 rounded-xl mb-4">
              <p className="text-cosmic-100 text-base leading-relaxed italic font-semibold">
                "{hi?(chart.personality?.core_trait_hi||chart.personality?.core_trait):chart.personality?.core_trait}"
              </p>
            </div>
            <div className="grid lg:grid-cols-2 gap-5">
              <div>
                <h4 className="font-cinzel text-amber-400 text-sm mb-3">{T('Personality Profile','व्यक्तित्व प्रोफाइल')}</h4>
                <div className="space-y-1">
                  <div className="py-1.5 border-b border-cosmic-800/30">
                    <p className="text-xs text-cosmic-400 mb-1">{T('Strengths','शक्तियाँ')}</p>
                    <p className="text-green-400 text-sm font-semibold">
                      {(hi?(chart.personality?.strengths_hi||chart.personality?.strengths):chart.personality?.strengths||[]).join(' • ')}
                    </p>
                  </div>
                  <div className="py-1.5 border-b border-cosmic-800/30">
                    <p className="text-xs text-cosmic-400 mb-1">{T('Blind Spots','कमज़ोरियाँ')}</p>
                    <p className="text-red-400 text-sm font-semibold">
                      {(hi?(chart.personality?.blind_spots_hi||chart.personality?.blind_spots):chart.personality?.blind_spots||[]).join(' • ')}
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-cinzel text-amber-400 text-sm mb-3">{T('Life Guidance','जीवन मार्गदर्शन')}</h4>
                <div className="space-y-1">
                  <InfoRow label={T('Decision Style','निर्णय शैली')} value={hi?(chart.personality?.decision_style_hi||chart.personality?.decision_style):chart.personality?.decision_style} />
                  <InfoRow label={T('Ideal Career','आदर्श करियर')}   value={hi?(chart.personality?.ideal_career_hi||chart.personality?.ideal_career):chart.personality?.ideal_career} />
                  <InfoRow label={T('Relationship Style','संबंध शैली')} value={hi?(chart.personality?.relationship_hi||chart.personality?.relationship_style):chart.personality?.relationship_style} />
                </div>
              </div>
            </div>
          </Section>

          {/* ─ Lucky Factors ─ */}
          <Section icon="🍀" title={T('Lucky Factors','शुभ कारक')} id="lucky">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                [T('Numbers','अंक'),    (chart.lucky?.numbers||[]).join(', '),              '🔢'],
                [T('Colors','रंग'),     (hi?(chart.lucky?.colors_hi||chart.lucky?.colors):chart.lucky?.colors||[]).join(', '),'🎨'],
                [T('Days','दिन'),       (chart.lucky?.days||[]).join(', '),                 '📅'],
                [T('Gemstone','रत्न'),  hi?(chart.gemstone_remedies?.primary_gem_hi||chart.gemstone_remedies?.primary_gem):chart.gemstone_remedies?.primary_gem,'💎'],
                [T('Metal','धातु'),     chart.lucky?.metal,                                '🪙'],
                [T('Direction','दिशा'), chart.lucky?.direction,                            '🧭'],
              ].map(([l,v,icon])=>(
                <div key={l} className="cosmic-card p-3 text-center border border-amber-700/30">
                  <div className="text-xl mb-1">{icon}</div>
                  <p className="text-xs text-amber-600/70 mb-1">{l}</p>
                  <p className="text-sm font-semibold text-cosmic-200">{v}</p>
                </div>
              ))}
            </div>
          </Section>




          {/* Complete 22-Section Summary */}
          <div className="cosmic-card p-6 border border-amber-700/30">
            <h3 className="font-cinzel text-xl font-bold text-amber-400 mb-2 flex items-center gap-2">
              <span>📋</span>
              {hi ? 'सम्पूर्ण कुण्डली सारांश — सभी 22 खंड' : 'Complete Kundali Summary — All 22 Sections'}
            </h3>
            <p className="text-cosmic-500 text-xs mb-6 italic">
              {hi ? 'आपके सम्पूर्ण वैदिक जन्म पत्रिका विश्लेषण का विस्तृत सारांश' : 'Detailed paragraph summary of your complete Vedic birth chart analysis'}
            </p>
            <div className="space-y-5 text-sm leading-relaxed">

              {/* 1 */}
              <div className="p-4 bg-amber-900/10 border-l-4 border-amber-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-amber-400 mb-2">
                  1. {hi ? 'पंचांग — जन्म का ब्रह्मांडीय वातावरण' : 'Panchang — Cosmic Birth Environment'}
                </h4>
                <p className="text-cosmic-200">
                  {hi ? (
                    <>आपका जन्म <strong className="text-white">{chart.panchang?.vaar_hi}</strong> को हुआ।
                    तिथि: <strong className="text-white">{chart.panchang?.tithi_hi}</strong>,
                    नक्षत्र: <strong className="text-white">{chart.panchang?.nakshatra_hi}</strong> पाद <strong className="text-amber-300">{chart.panchang?.nakshatra_pada}</strong>,
                    योग: <strong className="text-white">{chart.panchang?.yoga_hi}</strong>,
                    करण: <strong className="text-white">{chart.panchang?.karana_hi}</strong>।
                    ये पंचांग के पांच अंग आपके जन्म के ब्रह्मांडीय वातावरण को परिभाषित करते हैं और आपके भाग्य को गहरे कर्मिक स्तर पर प्रभावित करते हैं।</>
                  ) : (
                    <>You were born on <strong className="text-white">{chart.panchang?.vaar}</strong>.
                    Tithi: <strong className="text-white">{chart.panchang?.tithi}</strong>,
                    Nakshatra: <strong className="text-white">{chart.panchang?.nakshatra}</strong> Pada <strong className="text-amber-300">{chart.panchang?.nakshatra_pada}</strong>,
                    Yoga: <strong className="text-white">{chart.panchang?.yoga_panchang}</strong>,
                    Karana: <strong className="text-white">{chart.panchang?.karana}</strong>.
                    These five limbs of Panchang define the cosmic atmosphere at your birth and influence your destiny at a deep karmic level.</>
                  )}
                </p>
              </div>

              {/* 2 */}
              <div className="p-4 bg-blue-900/10 border-l-4 border-blue-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-blue-400 mb-2">
                  2. {hi ? 'अयनांश और गणना पद्धति' : 'Ayanamsa and Calculation Method'}
                </h4>
                <p className="text-cosmic-200">
                  {hi ? (
                    <>यह कुण्डली <strong className="text-white">{chart.ayanamsa?.system_hi||chart.ayanamsa?.system}</strong> पद्धति से
                    <strong className="text-amber-300"> {chart.ayanamsa?.value}</strong> अंश अयनांश मान के साथ बनाई गई है।
                    लाहिरी अयनांश भारत सरकार की आधिकारिक प्रणाली है और वैदिक ज्योतिष का मानक है।</>
                  ) : (
                    <>This Kundali uses <strong className="text-white">{chart.ayanamsa?.system}</strong> with
                    Ayanamsa value of <strong className="text-amber-300">{chart.ayanamsa?.value}</strong> degrees.
                    Lahiri Ayanamsa is the official system of the Government of India and the standard for authentic Vedic astrology.</>
                  )}
                </p>
              </div>

              {/* 3 */}
              <div className="p-4 bg-purple-900/10 border-l-4 border-purple-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-purple-400 mb-2">
                  3. {hi ? 'ग्रह स्थिति — सटीक अंश' : 'Planetary Positions — Exact Degrees'}
                </h4>
                <p className="text-cosmic-200">
                  {hi ? (
                    <>लग्न <strong className="text-amber-300">{chart.ascendant_hi||chart.ascendant}</strong> है जो <strong className="text-white">{chart.ascendant_lord}</strong> द्वारा शासित है।
                    सूर्य <strong className="text-amber-300">{chart.sun_sign_hi||chart.sun_sign}</strong> में और
                    चन्द्र <strong className="text-amber-300">{chart.moon_sign_hi||chart.moon_sign}</strong> में है।
                    {(chart.retrograde_planets||[]).length > 0 && <span> वक्री ग्रह: <strong className="text-red-400">{(chart.retrograde_planets||[]).join(', ')}</strong> — इनका प्रभाव आंतरिक और विलंबित होता है।</span>}
                    {(chart.combust_planets||[]).length > 0 && <span> अस्त ग्रह: <strong className="text-orange-400">{(chart.combust_planets||[]).map(cp => cp.planet).join(', ')}</strong> — सूर्य की निकटता से कमजोर।</span>}</>
                  ) : (
                    <>Ascendant is in <strong className="text-amber-300">{chart.ascendant}</strong> ruled by <strong className="text-white">{chart.ascendant_lord}</strong>.
                    Sun is in <strong className="text-amber-300">{chart.sun_sign}</strong> and Moon is in <strong className="text-amber-300">{chart.moon_sign}</strong>.
                    {(chart.retrograde_planets||[]).length > 0 && <span> Retrograde: <strong className="text-red-400">{(chart.retrograde_planets||[]).join(', ')}</strong> — internalized and delayed effect.</span>}
                    {(chart.combust_planets||[]).length > 0 && <span> Combust: <strong className="text-orange-400">{(chart.combust_planets||[]).map(cp => cp.planet).join(', ')}</strong> — weakened by Sun proximity.</span>}</>
                  )}
                </p>
              </div>

              {/* 4 */}
              <div className="p-4 bg-green-900/10 border-l-4 border-green-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-green-400 mb-2">
                  4. {hi ? 'षड्बल — ग्रह शक्ति विश्लेषण' : 'Shadbala — Planetary Strength Analysis'}
                </h4>
                <p className="text-cosmic-200">
                  {hi ? (
                    <>षड्बल विश्लेषण से पता चलता है कि आपकी कुण्डली में
                    <strong className="text-white"> {Object.entries(chart.shadbala||{}).filter(([,v])=>v.strong).map(([k])=>({Sun:'सूर्य',Moon:'चन्द्र',Mercury:'बुध',Venus:'शुक्र',Mars:'मंगल',Jupiter:'गुरु',Saturn:'शनि',Rahu:'राहु',Ketu:'केतु'}[k]||k)).join(', ')||'कोई नहीं'}</strong> बलवान ग्रह हैं।
                    शक्तिशाली ग्रह दशा काल में अपने फल पूर्णतः देते हैं। कमजोर ग्रहों को उपायों की आवश्यकता होती है।</>
                  ) : (
                    <>Shadbala analysis shows <strong className="text-white">{Object.entries(chart.shadbala||{}).filter(([,v])=>v.strong).map(([k])=>k).join(', ')||'none'}</strong> as strong planets.
                    Strong planets deliver results fully during their dasha periods. Weak planets need remedies to strengthen their positive effects.</>
                  )}
                </p>
              </div>

              {/* 5 */}
              <div className="p-4 bg-yellow-900/10 border-l-4 border-yellow-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-yellow-400 mb-2">
                  5. {hi ? 'अष्टकवर्ग — जीवन क्षेत्र अंक' : 'Ashtakavarga — Life Area Scores'}
                </h4>
                <p className="text-cosmic-200">
                  {hi
                    ? 'अष्टकवर्ग प्रणाली प्रत्येक ग्रह के लिए प्रत्येक राशि को बिंदु प्रदान करती है। 6 या अधिक बिंदु वाली राशियां अत्यंत अनुकूल हैं। सर्वाष्टकवर्ग में 28 से अधिक अंक वाली राशियां आपके सबसे मजबूत जीवन क्षेत्र हैं।'
                    : 'The Ashtakavarga system assigns bindus to each zodiac sign. Signs with 6 or more bindus are highly favorable. In Sarvashtakavarga, signs scoring 28+ points represent your strongest life areas, while signs below 20 present consistent challenges.'
                  }
                </p>
              </div>

              {/* 6-7 */}
              <div className="p-4 bg-pink-900/10 border-l-4 border-pink-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-pink-400 mb-2">
                  6-7. {hi ? 'दृष्टि और युति — ग्रहीय अंतःक्रिया' : 'Aspects and Conjunctions — Planetary Interactions'}
                </h4>
                <p className="text-cosmic-200">
                  {hi ? (
                    <>{(chart.conjunctions||[]).length > 0 ? (
                      <>आपकी कुण्डली में महत्वपूर्ण युतियां: {(chart.conjunctions||[]).map((cj,i) =>
                        <strong key={i} className="text-white mx-1">{cj.yoga_name} (भाव {cj.house})</strong>
                      )}। युतियां शक्तिशाली संयोजन बनाती हैं।</>
                    ) : 'आपकी कुण्डली में कोई प्रमुख ग्रह युति नहीं है। प्रत्येक ग्रह स्वतंत्र रूप से कार्य करता है।'}</>
                  ) : (
                    <>{(chart.conjunctions||[]).length > 0 ? (
                      <>Significant conjunctions: {(chart.conjunctions||[]).map((cj,i) =>
                        <strong key={i} className="text-white mx-1">{cj.yoga_name} (House {cj.house})</strong>
                      )}. Conjunctions create powerful combinations modifying planetary results.</>
                    ) : 'No major conjunctions found. Each planet operates independently, giving clearer and more predictable results.'}</>
                  )}
                </p>
              </div>

              {/* 8-9 */}
              <div className="p-4 bg-red-900/10 border-l-4 border-red-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-red-400 mb-2">
                  8-9. {hi ? 'वक्री और अस्त ग्रह' : 'Retrograde and Combust Planets'}
                </h4>
                <p className="text-cosmic-200">
                  {hi
                    ? 'वक्री ग्रह पिछले जन्मों के कर्म पाठ को दर्शाते हैं जिन्हें इस जीवन में हल करना है। उनकी ऊर्जा अंदर की ओर होती है, जिससे गहरी आत्म-परीक्षा और विलंबित परिणाम मिलते हैं। अस्त ग्रह सूर्य की तीव्र ऊर्जा से दब जाते हैं और उनके प्रभाव में कठिनाई हो सकती है।'
                    : 'Retrograde planets indicate karmic lessons from previous lives needing resolution in this lifetime. Their energy is directed inward, creating deep introspection and delayed but meaningful results. Combust planets lose natural strength due to overwhelming solar energy and may require extra effort to manifest their significations.'
                  }
                </p>
              </div>

              {/* 10 */}
              <div className="p-4 bg-indigo-900/10 border-l-4 border-indigo-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-indigo-400 mb-2">
                  10. {hi ? 'वर्ग चार्ट — जीवन के विभिन्न आयाम' : 'Divisional Charts — Multiple Life Dimensions'}
                </h4>
                <p className="text-cosmic-200">
                  {hi
                    ? 'D-9 नवांश आपके भाग्य और विवाह पैटर्न दिखाता है। D-10 दशांश करियर पथ प्रकट करता है। D-7 सप्तांश संतान और रचनात्मकता को दर्शाता है। D-12 द्वादशांश माता-पिता से संबंध दिखाता है। D-16 षोडशांश वाहन और विलास को दर्शाता है। ये वर्ग चार्ट मिलकर जन्म कुण्डली से परे एक सम्पूर्ण बहुआयामी विश्लेषण प्रदान करते हैं।'
                    : 'D-9 Navamsa shows your destiny and marriage patterns. D-10 Dashamsa reveals career trajectory. D-7 Saptamsa governs children and creativity. D-12 Dwadashamsa shows relationship with parents. D-16 Shodashamsa indicates vehicles and luxuries. Together these varga charts provide a complete multi-dimensional analysis beyond the main birth chart.'
                  }
                </p>
              </div>

              {/* 11-12 */}
              <div className="p-4 bg-teal-900/10 border-l-4 border-teal-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-teal-400 mb-2">
                  11-12. {hi ? 'आरूढ लग्न और उपग्रह' : 'Arudha Lagna and Upagrahas'}
                </h4>
                <p className="text-cosmic-200">
                  {hi ? (
                    <>आपका आरूढ लग्न <strong className="text-white">{chart.arudha_lagna?.sign}</strong> (भाव <strong className="text-amber-300">{chart.arudha_lagna?.house}</strong>) में है।
                    यह आपकी सामाजिक छवि दर्शाता है — दुनिया आपको कैसे देखती है, जो वास्तविक स्व से भिन्न हो सकती है।
                    गुलिक और मंडी छाया ग्रह हैं जिनका सूक्ष्म लेकिन महत्वपूर्ण कर्मिक प्रभाव होता है।</>
                  ) : (
                    <>Your Arudha Lagna is in <strong className="text-white">{chart.arudha_lagna?.sign}</strong> (House <strong className="text-amber-300">{chart.arudha_lagna?.house}</strong>).
                    This represents your public image — how the world perceives you, which may differ from your true self.
                    Gulika and Mandi are shadow planets with subtle but impactful karmic influences requiring careful attention.</>
                  )}
                </p>
              </div>

              {/* 13 */}
              <div className="p-4 bg-cyan-900/10 border-l-4 border-cyan-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-cyan-400 mb-2">
                  13. {hi ? 'वर्तमान गोचर विश्लेषण' : 'Current Transit Analysis (Gochar)'}
                </h4>
                <p className="text-cosmic-200">
                  {hi
                    ? 'वर्तमान ग्रहीय गोचर आपकी जन्म कुण्डली से तुलना करके सक्रिय प्रभावों की पहचान करता है। शनि का गोचर साढ़े साती (7.5 वर्ष) बनाता है। गुरु का गोचर जिन भावों को स्पर्श करता है वहां आशीर्वाद लाता है। राहु-केतु गोचर 18 महीने की कर्मिक धुरी बनाता है जो भाग्य को आकार देती है।'
                    : 'Current transits are compared against your natal chart to identify active influences. Saturn transit creates Sade Sati (7.5 year period) over Moon sign. Jupiter transit brings blessings to houses it touches. Rahu-Ketu transits create an 18-month karmic axis that shapes destiny and life direction.'
                  }
                </p>
              </div>

              {/* 14-15 */}
              <div className="p-4 bg-violet-900/10 border-l-4 border-violet-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-violet-400 mb-2">
                  14-15. {hi ? 'विंशोत्तरी और योगिनी दशा' : 'Vimshottari and Yogini Dasha'}
                </h4>
                <p className="text-cosmic-200">
                  {hi ? (
                    <>आप वर्तमान में <strong className="text-amber-300">{chart.dasha?.maha_dasha_hi||chart.dasha?.maha_dasha}</strong> महादशा में हैं जिसमें <strong className="text-white">{chart.dasha?.antardasha_hi||chart.dasha?.antardasha}</strong> अंतर्दशा सक्रिय है।
                    प्रत्यंतर दशा <strong className="text-white">{chart.dasha?.pratyantar_hi||chart.dasha?.pratyantar}</strong> मासिक स्तर की सटीक जानकारी देती है।
                    योगिनी दशा <strong className="text-amber-300">{chart.yogini_dasha?.current}</strong> ({chart.yogini_dasha?.years} वर्ष) सत्यापन के लिए समानांतर चलती है।</>
                  ) : (
                    <>You are in <strong className="text-amber-300">{chart.dasha?.maha_dasha}</strong> Maha Dasha with <strong className="text-white">{chart.dasha?.antardasha}</strong> Antardasha active.
                    Pratyantar Dasha of <strong className="text-white">{chart.dasha?.pratyantar}</strong> provides month-level timing precision.
                    Yogini Dasha of <strong className="text-amber-300">{chart.yogini_dasha?.current}</strong> ({chart.yogini_dasha?.years} years) runs concurrently for cross-verification.</>
                  )}
                </p>
              </div>

              {/* 16 */}
              <div className="p-4 bg-rose-900/10 border-l-4 border-rose-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-rose-400 mb-2">
                  16. {hi ? 'विशेष योग — दुर्लभ संयोजन' : 'Special Yogas — Rare Combinations'}
                </h4>
                <p className="text-cosmic-200">
                  {hi ? (
                    <>{(chart.special_yogas||[]).length > 0
                      ? <>{(chart.special_yogas||[]).map((y,i) => (
                          <span key={i}><strong className="text-white">{y.name_hi||y.name}</strong> — {y.description_hi||y.description}. </span>
                        ))}</>
                      : 'कोई असाधारण विशेष योग नहीं। आपकी कुण्डली की शक्ति मूलभूत ग्रह स्थितियों से आती है।'}
                    </>
                  ) : (
                    <>{(chart.special_yogas||[]).length > 0
                      ? <>{(chart.special_yogas||[]).map((y,i) => (
                          <span key={i}><strong className="text-white">{y.name}</strong> — {y.description}. </span>
                        ))}</>
                      : 'No special yogas detected. Your chart derives strength from the foundational planetary positions and house placements.'
                    }</>
                  )}
                </p>
              </div>

              {/* 17 */}
              <div className="p-4 bg-orange-900/10 border-l-4 border-orange-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-orange-400 mb-2">
                  17. {hi ? 'दोष विश्लेषण' : 'Dosha Analysis'}
                </h4>
                <p className="text-cosmic-200">
                  {hi ? (
                    <>{chart.dosha_analysis?.mangal_dosha?.present
                      ? <><strong className="text-red-400">मांगलिक दोष उपस्थित है</strong> — मंगल भाव {chart.dosha_analysis?.mangal_dosha?.mars_house} में है। तीव्रता: {chart.dosha_analysis?.mangal_dosha?.severity}। उपाय: <em className="text-amber-300">{chart.dosha_analysis?.mangal_dosha?.remedy_hi||chart.dosha_analysis?.mangal_dosha?.remedy}</em></>
                      : <><strong className="text-green-400">मांगलिक दोष नहीं है।</strong> {chart.dosha_analysis?.mangal_dosha?.description_hi||chart.dosha_analysis?.mangal_dosha?.description}</>
                    } काल सर्प अक्ष: राहु भाव <strong className="text-white">{chart.dosha_analysis?.kaal_sarp_dosha?.rahu_house}</strong>, केतु भाव <strong className="text-white">{chart.dosha_analysis?.kaal_sarp_dosha?.ketu_house}</strong> में है।</>
                  ) : (
                    <>{chart.dosha_analysis?.mangal_dosha?.present
                      ? <><strong className="text-red-400">Mangal Dosha present</strong> — Mars in House {chart.dosha_analysis?.mangal_dosha?.mars_house}. Severity: {chart.dosha_analysis?.mangal_dosha?.severity}. Remedy: <em className="text-amber-300">{chart.dosha_analysis?.mangal_dosha?.remedy}</em></>
                      : <><strong className="text-green-400">No Mangal Dosha.</strong> {chart.dosha_analysis?.mangal_dosha?.description}</>
                    } Kaal Sarp axis: Rahu in House <strong className="text-white">{chart.dosha_analysis?.kaal_sarp_dosha?.rahu_house}</strong>, Ketu in House <strong className="text-white">{chart.dosha_analysis?.kaal_sarp_dosha?.ketu_house}</strong>.</>
                  )}
                </p>
              </div>

              {/* 18 */}
              <div className="p-4 bg-amber-900/10 border-l-4 border-amber-500 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-amber-300 mb-2">
                  18. {hi ? 'भाव चलित चार्ट' : 'Bhava Chalita Chart'}
                </h4>
                <p className="text-cosmic-200">
                  {hi
                    ? 'भाव चलित चार्ट सटीक लग्न अंश से समान भाव प्रणाली का उपयोग करके ग्रहों की वास्तविक भाव स्थिति दिखाता है। कुछ ग्रह राशि चार्ट से भाव चलित में भाव बदल सकते हैं, जो भाव स्वामित्व और भाव-आधारित भविष्यवाणियों के लिए निर्णायक चार्ट माना जाता है।'
                    : 'The Bhava Chalita chart reveals actual house positions using the equal house system from the exact Ascendant degree. Some planets may shift houses from the Rashi chart. This is the definitive chart for determining house lordships and house-based predictions in the KP system.'
                  }
                </p>
              </div>

              {/* 19 */}
              <div className="p-4 bg-sky-900/10 border-l-4 border-sky-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-sky-400 mb-2">
                  19. {hi ? 'KP ज्योतिष (कृष्णमूर्ति पद्धति)' : 'KP Astrology (Krishnamurti Paddhati)'}
                </h4>
                <p className="text-cosmic-200">
                  {hi ? (
                    <>KP प्रणाली सटीक घटना समय के लिए सब-लॉर्ड अवधारणा का उपयोग करती है।
                    प्रथम भाव सिग्नीफिकेटर: <strong className="text-white">{chart.kp_astrology?.significator_1}</strong>,
                    सप्तम भाव सिग्नीफिकेटर: <strong className="text-white">{chart.kp_astrology?.significator_7}</strong>,
                    लग्न सब-लॉर्ड: <strong className="text-white">{chart.kp_astrology?.sublord_asc}</strong>।</>
                  ) : (
                    <>KP system uses Sub-Lord concept for precise event timing.
                    1st house significator: <strong className="text-white">{chart.kp_astrology?.significator_1}</strong>,
                    7th house significator: <strong className="text-white">{chart.kp_astrology?.significator_7}</strong>,
                    Ascendant Sub-Lord: <strong className="text-white">{chart.kp_astrology?.sublord_asc}</strong>.</>
                  )}
                </p>
              </div>

              {/* 20 */}
              <div className="p-4 bg-emerald-900/10 border-l-4 border-emerald-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-emerald-400 mb-2">
                  20. {hi ? 'जीवन क्षेत्र भविष्यवाणी' : 'Life Area Predictions'}
                </h4>
                <p className="text-cosmic-200">
                  <strong className="text-white">{hi ? 'करियर:' : 'Career:'}</strong>{' '}
                  {hi ? chart.life_predictions?.career?.summary_hi : chart.life_predictions?.career?.summary}.{' '}
                  <strong className="text-white">{hi ? 'विवाह:' : 'Marriage:'}</strong>{' '}
                  {hi ? chart.life_predictions?.marriage?.timing_hi : chart.life_predictions?.marriage?.timing}.{' '}
                  <strong className="text-white">{hi ? 'वित्त:' : 'Finance:'}</strong>{' '}
                  {hi ? chart.life_predictions?.finance?.overall_hi : chart.life_predictions?.finance?.overall}.{' '}
                  <strong className="text-white">{hi ? 'स्वास्थ्य:' : 'Health:'}</strong>{' '}
                  {hi ? chart.life_predictions?.health?.constitution_hi : chart.life_predictions?.health?.constitution}.{' '}
                  <strong className="text-white">{hi ? 'विदेश यात्रा:' : 'Foreign Travel:'}</strong>{' '}
                  {hi ? chart.life_predictions?.foreign_travel?.chance_hi : chart.life_predictions?.foreign_travel?.chance}.{' '}
                  <strong className="text-white">{hi ? 'आध्यात्मिकता:' : 'Spirituality:'}</strong>{' '}
                  {hi ? chart.life_predictions?.spirituality?.inclination_hi : chart.life_predictions?.spirituality?.inclination}.
                </p>
              </div>

              {/* 21 */}
              <div className="p-4 bg-fuchsia-900/10 border-l-4 border-fuchsia-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-fuchsia-400 mb-2">
                  21. {hi ? 'रत्न और उपाय विज्ञान' : 'Gemstone and Remedy Science'}
                </h4>
                <p className="text-cosmic-200">
                  {hi ? (
                    <>मुख्य रत्न: <strong className="text-amber-300">{chart.gemstone_remedies?.primary_gem_hi||chart.gemstone_remedies?.primary_gem}</strong> ({chart.gemstone_remedies?.weight}) —
                    लग्न स्वामी <strong className="text-white">{chart.gemstone_remedies?.primary_planet}</strong> को मजबूत करने के लिए।
                    <strong className="text-white"> {chart.gemstone_remedies?.finger_hi||chart.gemstone_remedies?.finger}</strong> पर <strong className="text-amber-300">{chart.gemstone_remedies?.day_hi||chart.gemstone_remedies?.day_to_wear}</strong> को पहनें।
                    मंत्र: <em className="text-amber-300">{chart.gemstone_remedies?.mantra_hi||chart.gemstone_remedies?.mantra}</em>।
                    नियमित वैदिक उपाय करने से अशुभ प्रभावों में काफी कमी आती है।</>
                  ) : (
                    <>Primary gemstone: <strong className="text-amber-300">{chart.gemstone_remedies?.primary_gem}</strong> ({chart.gemstone_remedies?.weight}) —
                    to strengthen ascendant lord <strong className="text-white">{chart.gemstone_remedies?.primary_planet}</strong>.
                    Wear on <strong className="text-white">{chart.gemstone_remedies?.finger}</strong> on <strong className="text-amber-300">{chart.gemstone_remedies?.day_to_wear}</strong>.
                    Mantra: <em className="text-amber-300">{chart.gemstone_remedies?.mantra}</em>.
                    Consistent Vedic remedies significantly reduce malefic influences and amplify benefic planetary energies.</>
                  )}
                </p>
              </div>

              {/* 22 */}
              <div className="p-4 bg-lime-900/10 border-l-4 border-lime-600 rounded-r-xl">
                <h4 className="font-cinzel font-bold text-lime-400 mb-2">
                  22. {hi ? 'व्यक्तित्व मनोविज्ञान और आत्म-विश्लेषण' : 'Personality Psychology and Self-Analysis'}
                </h4>
                <p className="text-cosmic-200">
                  {hi ? (
                    <>{chart.personality?.core_trait_hi||chart.personality?.core_trait}{' '}
                    शक्तियां: <strong className="text-green-400">{(chart.personality?.strengths_hi||chart.personality?.strengths||[]).join(', ')}</strong>.{' '}
                    सुधार क्षेत्र: <strong className="text-red-400">{(chart.personality?.blind_spots_hi||chart.personality?.blind_spots||[]).join(', ')}</strong>.{' '}
                    निर्णय शैली: <em className="text-amber-300">{chart.personality?.decision_style_hi||chart.personality?.decision_style}</em>.{' '}
                    आदर्श करियर: <strong className="text-white">{chart.personality?.ideal_career_hi||chart.personality?.ideal_career}</strong>.{' '}
                    संबंध: <em className="text-cosmic-300">{chart.personality?.relationship_hi||chart.personality?.relationship_style}</em>.</>
                  ) : (
                    <>{chart.personality?.core_trait}{' '}
                    Strengths: <strong className="text-green-400">{(chart.personality?.strengths||[]).join(', ')}</strong>.{' '}
                    Growth areas: <strong className="text-red-400">{(chart.personality?.blind_spots||[]).join(', ')}</strong>.{' '}
                    Decision style: <em className="text-amber-300">{chart.personality?.decision_style}</em>.{' '}
                    Ideal career: <strong className="text-white">{chart.personality?.ideal_career}</strong>.{' '}
                    In relationships: <em className="text-cosmic-300">{chart.personality?.relationship_style}</em>.</>
                  )}
                </p>
              </div>

            </div>
          </div>

        </div>
      )}
    </div>
  );
}

function buildNavamsa(chart) {
  const signs = ['Aries','Taurus','Gemini','Cancer','Leo','Virgo','Libra','Scorpio','Sagittarius','Capricorn','Aquarius','Pisces'];
  const base  = chart.house_signs || {};
  const result = {};
  for (let h = 1; h <= 12; h++) {
    const idx = signs.indexOf(base[h]);
    result[h] = signs[(idx + 3) % 12] || base[h];
  }
  return result;
}
